import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Plus, Mail, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import {
  ELECTRONICS_SUBCATEGORIES,
  PTAC_PRODUCTS,
  HOSPITALITY_TVS,
  HOSPITALITY_APPLIANCES,
  HOSPITALITY_ACCESSORIES,
  BRAND,
} from "@/data/products";
import { useQuote } from "@/context/QuoteContext";

const CATALOG_MAP = {
  "ptac-vtac": PTAC_PRODUCTS,
  "hospitality-tvs": HOSPITALITY_TVS,
  "hospitality-appliances": HOSPITALITY_APPLIANCES,
  "hospitality-accessories": HOSPITALITY_ACCESSORIES,
};

const slugFor = (p) => (p && p.slug) ? p.slug : (p.model || "").toLowerCase().replace(/\//g, "-");

const stripHtml = (s = "") =>
  s.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const setMetaDescription = (desc) => {
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", desc);
};

const ElectronicsProductDetailPage = () => {
  const { sub, productSlug } = useParams();
  const subcat = ELECTRONICS_SUBCATEGORIES.find((s) => s.slug === sub);
  const list = CATALOG_MAP[sub] || [];
  const product = list.find((p) => slugFor(p) === productSlug);
  const [activeImage, setActiveImage] = useState(0);
  const { addItem, setIsOpen } = useQuote();

  useEffect(() => {
    if (product) {
      const prev = document.title;
      document.title = `${product.name} | Hospitality Hub`;
      setMetaDescription(`${product.brand} · Model ${product.model} · ${stripHtml(product.shortDescription || product.description || "").slice(0, 155)}`);
      return () => { document.title = prev; };
    }
  }, [product]);

  if (!subcat || !product) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-3xl">Product not found</h1>
        <Link to="/products/electronics" className="text-[#D4AF37] underline mt-4 inline-block">Back to Electronics</Link>
      </main>
    );
  }

  const images = (product.images && product.images.length > 0) ? product.images : [product.image];

  const handleRequestQuote = () => {
    addItem({ id: product.id, name: `${product.name} (${product.model})`, image: product.image }, subcat.name);
    toast.success(`${product.model} added. Complete the quote form.`);
    setIsOpen(true);
  };

  const descriptionHtml = product.description || product.shortDescription || "";

  return (
    <main data-testid={`electronics-product-${slugFor(product)}`} className="bg-[#FAF9F6]">
      {/* Breadcrumb */}
      <div className="border-b border-[#D4AF37]/20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A4A4A] overflow-x-auto">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <Link to="/products/electronics" className="hover:text-[#D4AF37]">Electronics</Link>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <Link to={`/products/electronics/${sub}`} className="hover:text-[#D4AF37] whitespace-nowrap">{subcat.name}</Link>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <span className="text-[#D4AF37] whitespace-nowrap truncate">{product.model}</span>
        </div>
      </div>

      {/* Top section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative bg-white border border-[#E5E5E5] aspect-[4/3] overflow-hidden">
            <img
              key={activeImage}
              data-testid="product-main-image"
              src={images[activeImage]}
              alt={product.name}
              className="w-full h-full object-contain p-6 hh-fade-in"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square overflow-hidden border transition-all ${
                    i === activeImage ? "border-[#D4AF37]" : "border-[#E5E5E5] hover:border-[#D4AF37]/60"
                  }`}
                >
                  <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-contain bg-white p-2" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">{product.brand}</p>
          <h1 data-testid="product-title" className="font-serif-display text-3xl lg:text-4xl mt-3 leading-[1.15]">
            {product.name}
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#4A4A4A]">Model: <span className="text-[#1A1A1A]">{product.model}</span></p>
          {product.pack && (
            <p data-testid="product-pack" className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-3 py-1.5">
              {product.pack}
            </p>
          )}

          <div className="mt-8 text-[#4A4A4A] leading-relaxed prose-sm max-w-none" data-testid="product-highlights">
            <div dangerouslySetInnerHTML={{ __html: product.description || product.shortDescription || "" }} />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              data-testid="request-quote-btn"
              onClick={handleRequestQuote}
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Mail className="w-4 h-4" /> Request a Quote
            </button>
            <a
              href={`tel:${BRAND.phone}`}
              data-testid="call-btn"
              className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-[#4A4A4A] uppercase tracking-[0.18em] border-t border-[#D4AF37]/20 pt-6">
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Factory Direct</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> 24h Response</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Nationwide Shipping</span>
          </div>
        </div>
      </section>

      {/* Description */}
      {descriptionHtml && (
        <section className="bg-white border-y border-[#D4AF37]/20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Product Description</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl mb-8">About this product</h2>
            <div
              data-testid="product-description"
              className="prose prose-lg max-w-3xl text-[#4A4A4A] leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_strong]:text-[#1A1A1A] [&_b]:text-[#1A1A1A] [&_p]:my-3"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          </div>
        </section>
      )}

      {/* Specifications summary */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Specifications</p>
        <h2 className="font-serif-display text-3xl lg:text-4xl mb-8">At a glance</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-0" data-testid="product-specs">
          {[
            ["Brand", product.brand],
            ["Model Number", product.model],
            ...(product.pack ? [["Pack Quantity", product.pack]] : []),
            ["Category", subcat.name],
            ["Source", product.source || "TWS Transworld"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between py-4 border-b border-[#E5E5E5]">
              <dt className="text-xs uppercase tracking-[0.2em] text-[#4A4A4A]">{k}</dt>
              <dd className="font-serif-display text-lg text-[#1A1A1A] text-right ml-6">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 text-xs text-[#4A4A4A]">
          Full manufacturer spec sheet available on request. Reply to the quote email or call <a href={`tel:${BRAND.phone}`} className="text-[#D4AF37] hover:underline">{BRAND.phone}</a>.
        </p>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-[#1A1A1A] text-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif-display text-3xl leading-tight">Need pricing on {product.model}?</h3>
            <p className="text-white/70 mt-3">Factory-direct pricing. Get in touch and we'll respond within 24 business hours.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <button
              onClick={handleRequestQuote}
              data-testid="bottom-request-quote"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Plus className="w-4 h-4" /> Request a Quote
            </button>
            <Link
              to={`/products/electronics/${sub}`}
              data-testid="back-to-sub"
              className="inline-flex items-center gap-2 border border-white/40 hover:bg-white hover:text-[#1A1A1A] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              Back to {subcat.name}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ElectronicsProductDetailPage;
